import { useState } from 'react';
import {
  Card,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { listAdminUsers } from '@/api/endpoints';
import type { AdminUser, Page } from '@/api/types';
import { PageHeader } from '@/components/PageHeader';

export function AdminUsersPage() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const offset = page * pageSize;

  const users = useQuery<Page<AdminUser>>({
    queryKey: ['admin', 'users', pageSize, offset],
    queryFn: () => listAdminUsers(pageSize, offset),
  });

  return (
    <Container maxWidth="lg" disableGutters>
      <PageHeader
        title="Users"
        subtitle="JIT-provisioned by your IdP on first login. is_admin is recomputed from the configured admin group on each login."
      />

      {users.isLoading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {users.isSuccess && (
        <TableContainer component={Card} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Display name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.data.items.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.display_name ?? '—'}</TableCell>
                  <TableCell>
                    {u.is_admin ? (
                      <Chip size="small" color="secondary" label="Admin" />
                    ) : (
                      <Chip size="small" variant="outlined" label="User" />
                    )}
                  </TableCell>
                  <TableCell>{format(new Date(u.created_at), 'PPp')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={users.data.total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}
    </Container>
  );
}
